import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Image, Input, InputNumber, Modal, Space, Table, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { DeleteOutlined, InboxOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import {
  RECEIVING_REASON_LABEL,
  RECEIVING_STATUS_LABEL,
  ReceivingCheckResult,
  ReceivingException,
  ReceivingExtraInput,
  ReceivingPlanEntry,
  ReceivingRowInput,
  InspectionExecution,
} from '../../../../types/returnInspection';
import {
  cancelReceivingException,
  checkReceiving,
  createReceivingException,
  getReceivingInspectionRules,
  submitReceivingPlan,
  uploadReceivingPhoto,
} from '../../../../services/returnInspection';
import type { BoardPlacement } from '../../../../utils/sortingBoardBatch';
import type { LocalSpeechLang } from '../../../../utils/localSpeech';
import ReceivingPlanEditor, { FetchRuleHits } from '../../../return-inspection/ReceivingPlanEditor';
import ReceivingPlanSummary from '../../../return-inspection/ReceivingPlanSummary';
import ReturnInspectionWizard from './ReturnInspectionWizard';
import { getAttachmentUrl } from '../../../../utils/attachmentUrl';
import { printReceivingExceptionLabel } from '../../../../utils/receivingExceptionLabel';

const { Text, Paragraph } = Typography;

interface ReturnLineLike {
  id: number;
  inventory_unit: number;
  inventory_unit_barcode: string;
  inventory_unit_detail?: {
    remarks?: string;
    content?: {
      items?: Array<{
        product_name?: string;
        product_code?: string;
        quantity?: number;
        sorting_codes?: Array<{ code: string; description?: string }>;
      }>;
    };
  };
  quantity: number;
  reference_photos?: { product?: string[]; packaging?: string[] };
}

export interface ReturnReceivingForecast {
  id: number;
  system_code: string;
  /** 项目 ID（预报外物品指定库存单元时用来列出本项目的散货库存单元） */
  project?: number;
  project_detail?: { project_code?: string; name?: string };
  return_lines?: ReturnLineLike[];
  open_receiving_exception?: ReceivingException | null;
}

interface Props {
  forecast: ReturnReceivingForecast;
  trackingNumber: string;
  /** 关闭扫描结果弹窗 */
  onClose: () => void;
  /** 初检提交成功后：外层把入库的货加入分拣看板、触发建层轮询，并返回各库存单元的临时分拣仓位 */
  onExecuted?: (execution: InspectionExecution) => BoardPlacement[] | void;
  speechLang?: LocalSpeechLang;
  speechEnabled?: boolean;
}

interface RowState {
  actual: number | null;
}

interface ExtraState {
  description: string;
  quantity: number | null;
}

const MAX_PHOTOS = 20;

const formatTime = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-CN', { hour12: false });
};

const errorText = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  const first = Object.values(data)[0] as any;
  if (Array.isArray(first)) return typeof first[0] === 'string' ? first[0] : fallback;
  if (typeof first === 'string') return first;
  return fallback;
};

/**
 * 退货入库初检 - 第一步：收货核对。
 * 仓库对着实物逐行填写实收数量、确认是否与预报一致（退货商品常常没有条码，不依赖扫码）；
 * 一致与否由后端判定。不一致时留证（至少 1 张照片）、创建收货异常、中断入库并打印标签贴在原包装上。
 * 见 docs/退货异常库存(AIU)处理开发方案-261001.md 第 5.3 节。
 */
const ReturnReceivingPanel: React.FC<Props> = ({ forecast, trackingNumber, onClose, onExecuted, speechLang, speechEnabled }) => {
  const lines = useMemo(() => forecast.return_lines || [], [forecast.return_lines]);
  // 进行中的收货异常（待客户方案 / 方案已确认）；处理或撤销后变为 null
  const [openException, setOpenException] = useState<ReceivingException | null>(forecast.open_receiving_exception || null);
  const [view, setView] = useState<'main' | 'wizard'>('main');
  const [wizardData, setWizardData] = useState<{ plan: ReceivingPlanEntry[]; recount?: { rows: ReceivingRowInput[]; extras: ReceivingExtraInput[] } } | null>(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const [rows, setRows] = useState<Record<number, RowState>>(() =>
    Object.fromEntries(lines.map(line => [line.inventory_unit, { actual: null } as RowState]))
  );
  const [extras, setExtras] = useState<ExtraState[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [verdict, setVerdict] = useState<ReceivingCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ReceivingException | null>(null);

  const projectLabel = `${forecast.project_detail?.project_code || ''} ${forecast.project_detail?.name || ''}`.trim();

  const invalidate = () => setVerdict(null);

  const updateRow = (unitId: number, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [unitId]: { ...prev[unitId], ...patch } }));
    invalidate();
  };

  const updateExtra = (index: number, patch: Partial<ExtraState>) => {
    setExtras(prev => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
    invalidate();
  };

  const allRowsFilled = lines.every(line => {
    const row = rows[line.inventory_unit];
    return row && row.actual !== null && row.actual !== undefined;
  });

  const buildPayload = (): { rows: Array<{ inventory_unit: number; actual_quantity: number }>; extras: ReceivingExtraInput[] } | null => {
    const payloadRows = lines.map(line => {
      const row = rows[line.inventory_unit];
      return { inventory_unit: line.inventory_unit, actual_quantity: Number(row.actual) };
    });
    const payloadExtras: ReceivingExtraInput[] = [];
    for (const extra of extras) {
      const description = extra.description.trim();
      const quantity = extra.quantity;
      if (!description && !quantity) continue; // 完全空白的行直接忽略
      if (!description || !quantity || quantity < 1) {
        message.warning('预报外物品需要同时填写描述和数量（至少 1 件），或删除该行');
        return null;
      }
      payloadExtras.push({ description, quantity });
    }
    return { rows: payloadRows, extras: payloadExtras };
  };

  const uploadFiles = async (files: File[]) => {
    const images = files.filter(f => f.type.startsWith('image/'));
    if (images.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      message.warning(`最多上传 ${MAX_PHOTOS} 张照片`);
      return;
    }
    const batch = images.slice(0, room);
    if (batch.length < images.length) message.warning(`最多上传 ${MAX_PHOTOS} 张照片，多出的已忽略`);
    setUploading(n => n + batch.length);
    for (const file of batch) {
      try {
        const url = await uploadReceivingPhoto(file);
        setPhotos(prev => [...prev, url]);
      } catch (err: any) {
        message.error(errorText(err, `照片「${file.name}」上传失败`));
      } finally {
        setUploading(n => n - 1);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const files: File[] = [];
    Array.from(e.clipboardData?.items || []).forEach(item => {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    });
    if (files.length > 0) {
      e.preventDefault();
      uploadFiles(files);
    }
  };

  const handleCheck = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setChecking(true);
    try {
      const result = await checkReceiving({ forecast: forecast.id, ...payload });
      setVerdict(result);
    } catch (err: any) {
      message.error(errorText(err, '核对失败，请重试'));
    } finally {
      setChecking(false);
    }
  };

  // 只能由用户点击"打印标签"触发（在单独的新窗口里打印，不会卡住当前页面）
  const printLabel = (exception: ReceivingException) => {
    try {
      printReceivingExceptionLabel({
        forecastCode: forecast.system_code,
        trackingNumber,
        projectLabel,
        reasonLabels: exception.reason_labels,
        createdAt: exception.created_at,
      });
    } catch (err: any) {
      console.error('[收货异常标签] 打印失败', err);
      message.error(err?.message || '标签打印失败，请重试');
    }
  };

  const doCreateException = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setCreating(true);
    try {
      const exception = await createReceivingException({ forecast: forecast.id, ...payload, photos, remarks: remarks.trim() });
      setCreated(exception);
      message.success('已创建收货异常，本次入库已中断，请打印标签');
    } catch (err: any) {
      message.error(errorText(err, '创建收货异常失败，请重试'));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateException = () => {
    if (photos.length === 0) {
      message.warning('核对不一致时必须至少上传 1 张留证照片');
      return;
    }
    Modal.confirm({
      title: '中断入库并创建收货异常？',
      content: '将不会创建入库记录，货物请暂存并贴上异常标签，等待客户确认。客户会在预报列表里看到"退货入库异常，请选择处理方案"。',
      okText: '中断入库',
      cancelText: '再检查一下',
      // 不返回 Promise：确认框立刻关闭，创建过程由下面按钮的 loading 状态表示
      onOk: () => { void doCreateException(); },
    });
  };

  const unitLabels = useMemo(
    () => Object.fromEntries(lines.map(line => {
      const items = line.inventory_unit_detail?.content?.items || [];
      const names = items.map(item => item.product_name).filter(Boolean).join('、');
      // 只显示第一个分拣条码：LIU-26101-1 LU2产品1(X001ABC456)
      let sortingCode = '';
      items.forEach(item => {
        if (!sortingCode) sortingCode = (item.sorting_codes || []).map(c => c.code).find(Boolean) || '';
      });
      return [line.inventory_unit, `${line.inventory_unit_barcode}${names ? ' ' + names : ''}${sortingCode ? '(' + sortingCode + ')' : ''}`];
    })),
    [lines]
  );

  // 仓库代录入方案时，显示预报外物品指定的库存单元当前适用的规则
  const fetchRuleHits: FetchRuleHits = async unitIds => {
    const hits = await getReceivingInspectionRules(forecast.id, unitIds);
    const result: Record<number, { source: 'unit' | 'project' | 'customer' | null; mode: any }> = {};
    unitIds.forEach(id => { result[id] = { source: null, mode: null }; });
    hits.forEach(hit => { result[hit.inventory_unit_id] = { source: hit.source, mode: hit.rule.mode }; });
    return result;
  };

  const handleSubmitPlan = async (payload: { items: any[]; acknowledged: boolean; note: string }) => {
    if (!openException) return;
    setPlanSubmitting(true);
    try {
      const result = await submitReceivingPlan(openException.id, payload);
      setOpenException(result);
      setPlanModalOpen(false);
      message.success('处理方案已保存');
    } catch (err: any) {
      message.error(errorText(err, '保存处理方案失败，请重试'));
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleCancelException = async () => {
    if (!openException) return;
    if (!cancelNote.trim()) {
      message.warning('请填写撤销原因（例如：重新清点后发现数错了）');
      return;
    }
    setCancelling(true);
    try {
      await cancelReceivingException(openException.id, cancelNote.trim());
      message.success('异常已撤销，请重新核对');
      setOpenException(null);
      setVerdict(null);
      setCancelOpen(false);
      setCancelNote('');
    } catch (err: any) {
      message.error(errorText(err, '撤销失败，请重试'));
    } finally {
      setCancelling(false);
    }
  };

  // 核对一致：自动生成"全部按规则初检"的方案
  const startNormalInspection = () => {
    const payload = buildPayload();
    if (!payload || !verdict) return;
    const plan: ReceivingPlanEntry[] = verdict.rows
      .filter(row => row.actual_quantity > 0)
      .map(row => ({
        key: `line:${row.inventory_unit}`,
        source: 'forecast_line' as const,
        inventory_unit: row.inventory_unit,
        description: row.inventory_unit_barcode,
        inventory_unit_barcode: row.inventory_unit_barcode,
        quantity: row.actual_quantity,
        mode: 'inspect' as const,
      }));
    setWizardData({ plan, recount: payload });
    setView('wizard');
  };

  // 按客户确认的方案执行：不再重新核对，数量以异常记录里的实收清单为准
  const startPlanExecution = () => {
    if (!openException) return;
    setWizardData({ plan: openException.plan });
    setView('wizard');
  };

  // 多个值时只显示第 1 个 + "等N个"，鼠标悬停显示完整内容
  const firstWithCount = (values: string[], full?: React.ReactNode) => {
    if (values.length === 0) return <Text type="secondary">-</Text>;
    if (values.length === 1) return <span>{values[0]}</span>;
    return (
      <Tooltip title={full ?? values.map((v, i) => <div key={i}>{v}</div>)}>
        <span style={{ cursor: 'help' }}>{values[0]}等{values.length}个</span>
      </Tooltip>
    );
  };

  const unitInfo = (line: ReturnLineLike) => {
    const items = line.inventory_unit_detail?.content?.items || [];
    const uniq = (list: Array<string | undefined>) => Array.from(new Set(list.map(v => (v || '').trim()).filter(Boolean)));
    const names = uniq(items.map(item => item.product_name));
    const customCodes = uniq(items.map(item => item.product_code));
    const sortingCodes: Array<{ code: string; description?: string }> = [];
    items.forEach(item => (item.sorting_codes || []).forEach(sc => {
      if (sc.code && !sortingCodes.some(existing => existing.code === sc.code)) sortingCodes.push(sc);
    }));
    const rowStyle: React.CSSProperties = { fontSize: 12, lineHeight: '20px' };
    const labelStyle: React.CSSProperties = { color: '#8c8c8c', marginRight: 4 };
    return (
      <div>
        <div style={rowStyle}><span style={labelStyle}>系统编码：</span><Text code>{line.inventory_unit_barcode}</Text></div>
        <div style={rowStyle}><span style={labelStyle}>产品名称：</span>{firstWithCount(names)}</div>
        <div style={rowStyle}><span style={labelStyle}>自定义编码：</span>{firstWithCount(customCodes)}</div>
        <div style={rowStyle}>
          <span style={labelStyle}>分拣条码：</span>
          {firstWithCount(
            sortingCodes.map(sc => sc.code),
            sortingCodes.map(sc => <div key={sc.code}>{sc.code}{sc.description ? ` ${sc.description}` : ''}</div>)
          )}
        </div>
      </div>
    );
  };

  const referencePhotos = (line: ReturnLineLike) => {
    const product = line.reference_photos?.product || [];
    const packaging = line.reference_photos?.packaging || [];
    if (product.length === 0 && packaging.length === 0) return <Text type="secondary" style={{ fontSize: 12 }}>无参考照片</Text>;
    return (
      <Image.PreviewGroup>
        <Space size={4} wrap>
          {product.map(url => (
            <div key={`p-${url}`} style={{ textAlign: 'center' }}>
              <Image src={getAttachmentUrl({ url })} width={52} height={52} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <div style={{ fontSize: 10, color: '#8c8c8c' }}>产品</div>
            </div>
          ))}
          {packaging.map(url => (
            <div key={`k-${url}`} style={{ textAlign: 'center' }}>
              <Image src={getAttachmentUrl({ url })} width={52} height={52} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <div style={{ fontSize: 10, color: '#8c8c8c' }}>外包装</div>
            </div>
          ))}
        </Space>
      </Image.PreviewGroup>
    );
  };

  // ---------------- 执行向导 ----------------
  if (view === 'wizard' && wizardData) {
    return (
      <ReturnInspectionWizard
        forecast={forecast}
        plan={wizardData.plan}
        recount={wizardData.recount}
        unitLabels={unitLabels}
        trackingNumber={trackingNumber}
        onBack={() => setView('main')}
        onClose={onClose}
        onExecuted={onExecuted}
        speechLang={speechLang}
        speechEnabled={speechEnabled}
      />
    );
  }

  // ---------------- 创建完成：打印标签 ----------------
  if (created) {
    return (
      <Card size="small" title="退货入库初检">
        <Alert
          type="warning"
          showIcon
          message="本次入库已中断，收货异常已记录"
          description={
            <div>
              <Paragraph style={{ marginBottom: 4 }}>
                预报 <Text code>{forecast.system_code}</Text>，异常原因：{created.reason_labels.join('、')}。
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                请点击下面的"打印标签"，把标签贴在原包装外面，货物暂存，等客户确认后再次扫描该物流单号处理。客户会在预报列表和详情里看到异常标记。
              </Paragraph>
            </div>
          }
        />
        <Space style={{ marginTop: 16 }}>
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => printLabel(created)}>打印标签</Button>
          <Button onClick={onClose}>完成并关闭</Button>
        </Space>
      </Card>
    );
  }

  // ---------------- 进行中的收货异常：等客户方案 / 按方案执行 ----------------
  if (openException) {
    const snapshotRows = openException.snapshot?.rows || [];
    const waitingDays = Math.max(0, Math.floor((Date.now() - new Date(openException.created_at).getTime()) / 86400000));
    const planReady = openException.status === 'plan_ready';
    return (
      <Card size="small" title="退货入库初检">
        <Alert
          type={planReady ? 'warning' : 'error'}
          showIcon
          message={`该预报有进行中的收货异常：${RECEIVING_STATUS_LABEL[openException.status]}`}
          description={
            <div>
              <div>原因：{openException.reason_labels.join('、')}</div>
              <div>创建时间：{formatTime(openException.created_at)}{openException.created_by_name ? `（${openException.created_by_name}）` : ''}，已等待 {waitingDays} 天</div>
              {openException.remarks ? <div>备注：{openException.remarks}</div> : null}
              <div style={{ marginTop: 6 }}>
                上次核对：预报 {openException.snapshot?.forecast_total ?? '-'} 件，实收 {openException.snapshot?.actual_total ?? '-'} 件
                {snapshotRows.filter(r => !r.ok).length > 0 ? `，${snapshotRows.filter(r => !r.ok).length} 行数量不符` : ''}
                {(openException.snapshot?.extras || []).length > 0 ? `，预报外物品 ${(openException.snapshot?.extras || []).length} 项` : ''}
              </div>
              {openException.photos.length > 0 && (
                <Image.PreviewGroup>
                  <Space size={4} wrap style={{ marginTop: 6 }}>
                    {openException.photos.map(url => (
                      <Image key={url} src={getAttachmentUrl({ url })} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              )}
            </div>
          }
        />
        <div style={{ marginTop: 16 }}>
          {planReady ? (
            <>
              <Text strong>客户确认的处理方案</Text>
              <div style={{ marginTop: 8 }}>
                <ReceivingPlanSummary exception={openException} unitLabels={unitLabels} />
              </div>
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                按客户确认的方案直接执行，数量以异常记录里的实收清单为准。
              </Paragraph>
            </>
          ) : (
            <Paragraph style={{ marginBottom: 0 }}>
              等待客户选择处理方案（客户可以在预报详情里选择）。客户线下已经确认的，可以点"代客户录入方案"，并写明客户是怎么确认的。
            </Paragraph>
          )}
          <Space style={{ marginTop: 12 }} wrap>
            {planReady && <Button type="primary" onClick={startPlanExecution}>开始执行方案</Button>}
            <Button type={planReady ? 'default' : 'primary'} onClick={() => setPlanModalOpen(true)}>{planReady ? '修改方案' : '代客户录入方案'}</Button>
            <Button icon={<PrinterOutlined />} onClick={() => printLabel(openException)}>打印标签</Button>
            <Button danger onClick={() => setCancelOpen(true)}>撤销异常（重新清点）</Button>
            <Button onClick={onClose}>关闭</Button>
          </Space>
        </div>
        <Modal
          title="处理方案"
          open={planModalOpen}
          onCancel={() => setPlanModalOpen(false)}
          footer={null}
          width={960}
          destroyOnClose
        >
          <ReceivingPlanEditor
            exception={openException}
            projectId={forecast.project}
            unitLabels={unitLabels}
            actor="warehouse"
            fetchRuleHits={fetchRuleHits}
            submitting={planSubmitting}
            onSubmit={handleSubmitPlan}
            onCancel={() => setPlanModalOpen(false)}
          />
        </Modal>
        <Modal
          title="撤销异常（重新清点）"
          open={cancelOpen}
          onCancel={() => setCancelOpen(false)}
          onOk={handleCancelException}
          okText="撤销异常"
          okButtonProps={{ danger: true, loading: cancelling }}
          cancelText="不撤销"
          destroyOnClose
        >
          <Paragraph type="secondary">
            适用于仓库发现之前清点有误的情况：撤销后这个异常作废（记录保留），可以重新核对。已经由客户确认的方案也会作废。
          </Paragraph>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="撤销原因（必填），例如：重新清点后发现数错了"
            value={cancelNote}
            onChange={e => setCancelNote(e.target.value)}
          />
        </Modal>
      </Card>
    );
  }

  const hasRowMismatch = (unitId: number) => {
    const row = verdict?.rows.find(r => r.inventory_unit === unitId);
    return row ? !row.ok : false;
  };

  const columns = [
    {
      title: '库存单元',
      key: 'unit',
      width: 300,
      render: (_: unknown, line: ReturnLineLike) => unitInfo(line),
    },
    { title: '参考照片', key: 'photos', width: 190, render: (_: unknown, line: ReturnLineLike) => referencePhotos(line) },
    { title: '预报数量', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'center' as const },
    {
      title: '实收数量',
      key: 'actual',
      width: 120,
      render: (_: unknown, line: ReturnLineLike) => (
        <InputNumber
          min={0}
          max={1000000}
          precision={0}
          placeholder="必填"
          style={{ width: 96 }}
          value={rows[line.inventory_unit]?.actual ?? null}
          status={hasRowMismatch(line.inventory_unit) ? 'error' : undefined}
          onChange={value => updateRow(line.inventory_unit, { actual: typeof value === 'number' ? value : null })}
        />
      ),
    },
  ];

  return (
    <Card size="small" title="退货入库初检 · 收货核对" onPaste={handlePaste}>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        对着实物逐行填写：先对照产品名称、参考照片，确认收到的就是这种商品，再填实收数量（必须手填；没收到填 0）。收到的不是预报里的商品，请在下方添加为"预报外物品"。
      </Paragraph>
      <Table
        size="small"
        pagination={false}
        rowKey="inventory_unit"
        dataSource={lines}
        columns={columns}
        rowClassName={(line: ReturnLineLike) => (hasRowMismatch(line.inventory_unit) ? 'ant-table-row-selected' : '')}
      />

      <div style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 8 }}>
          <Text strong>预报外物品</Text>
          <Button size="small" icon={<PlusOutlined />} onClick={() => { setExtras(prev => [...prev, { description: '', quantity: null }]); invalidate(); }}>
            添加预报外物品
          </Button>
        </Space>
        {extras.map((extra, index) => (
          <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="start">
            <Input
              style={{ width: 360 }}
              maxLength={200}
              placeholder="物品描述，例如：一副无线耳机"
              value={extra.description}
              onChange={e => updateExtra(index, { description: e.target.value })}
            />
            <InputNumber
              min={1}
              max={1000000}
              precision={0}
              placeholder="数量"
              value={extra.quantity}
              onChange={value => updateExtra(index, { quantity: typeof value === 'number' ? value : null })}
            />
            <Button icon={<DeleteOutlined />} onClick={() => { setExtras(prev => prev.filter((_, i) => i !== index)); invalidate(); }} />
          </Space>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <Text strong>留证照片</Text>
        <Text type="secondary" style={{ marginLeft: 8 }}>
          整单实物照片；核对不一致时必须至少 1 张。支持多选、拖拽，也可以先点一下此卡片再按 Ctrl+V 粘贴图片。
        </Text>
        <Upload.Dragger
          style={{ marginTop: 8 }}
          multiple
          accept="image/*"
          showUploadList={false}
          disabled={photos.length >= MAX_PHOTOS}
          customRequest={({ file, onSuccess }) => {
            uploadFiles([file as File]).finally(() => onSuccess && onSuccess('ok'));
          }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}><InboxOutlined /></p>
          <p className="ant-upload-text" style={{ fontSize: 14 }}>
            点击或拖拽图片到这里上传{uploading > 0 ? `（上传中 ${uploading} 张…）` : ''}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>已上传 {photos.length} / {MAX_PHOTOS} 张，每张不超过 10MB（jpg / png / webp）</p>
        </Upload.Dragger>
        {photos.length > 0 && (
          <Image.PreviewGroup>
            <Space size={8} wrap style={{ marginTop: 8 }}>
              {photos.map(url => (
                <div key={url} style={{ position: 'relative' }}>
                  <Image src={getAttachmentUrl({ url })} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
                  <Button
                    size="small"
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6 }}
                    onClick={() => setPhotos(prev => prev.filter(p => p !== url))}
                  />
                </div>
              ))}
            </Space>
          </Image.PreviewGroup>
        )}
      </div>

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" loading={checking} disabled={!allRowsFilled} onClick={handleCheck}>
          核对
        </Button>
        {!allRowsFilled && <Text type="secondary">请先填完每一行的实收数量（没收到填 0）</Text>}
      </Space>

      {verdict && verdict.consistent && (
        <Alert
          style={{ marginTop: 16 }}
          type="success"
          showIcon
          message="核对一致"
          description={
            <div>
              实收 {verdict.actual_total} 件，与预报一致，可以按规则逐项初检。
              <div style={{ marginTop: 8 }}>
                <Button type="primary" onClick={startNormalInspection}>开始初检</Button>
              </div>
            </div>
          }
        />
      )}

      {verdict && !verdict.consistent && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="核对不一致"
          description={
            <div>
              <div style={{ marginBottom: 6 }}>
                {verdict.reasons.map(reason => (
                  <Tag color="red" key={reason}>{RECEIVING_REASON_LABEL[reason]}</Tag>
                ))}
                预报 {verdict.forecast_total} 件，实收 {verdict.actual_total} 件
              </div>
              {verdict.rows.filter(r => !r.ok).map(r => (
                <div key={r.inventory_unit} style={{ fontSize: 12 }}>
                  <Text code>{r.inventory_unit_barcode}</Text>
                  {!r.quantity_ok ? `：预报 ${r.forecast_quantity}，实收 ${r.actual_quantity}` : ''}
                </div>
              ))}
              {verdict.extras.map((e, i) => (
                <div key={i} style={{ fontSize: 12 }}>预报外：{e.description} ×{e.quantity}</div>
              ))}
              <div style={{ marginTop: 8 }}>
                如果只是清点有误，直接修改上面的数据重新核对；确实不一致，请上传留证照片后中断入库。
              </div>
              <Input.TextArea
                style={{ marginTop: 8 }}
                rows={2}
                maxLength={500}
                placeholder="备注（选填），例如：少了一件，外箱有破损"
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
              />
              <Space style={{ marginTop: 8 }}>
                <Button danger type="primary" loading={creating} onClick={handleCreateException}>
                  中断入库并创建收货异常
                </Button>
              </Space>
            </div>
          }
        />
      )}
    </Card>
  );
};

export default ReturnReceivingPanel;
